import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

// Plan approval data layer — Script Approval and Video Approval, one workflow
// with two kinds (components/approvals/approvalKinds.js; `kind.api` /
// `kind.reviewApi` are the backend mounts). Each workflow is driven from two
// places, so every item-level hook takes a `scope`:
//   teamScope(kind, planId)                  — the logged-in team (/api/scripts, /api/videos)
//   reviewScope(kind, token, reviewerName)   — a public share link (/api/script-review, /api/video-review)
// The workflow rules themselves live server-side (helpers/approvalWorkflow.js in
// pluginbackendnode) — nothing here re-implements them.

export const teamScope = (kind, planId) => ({ kind, mode: "team", planId });
export const reviewScope = (kind, token, reviewerName) => ({ kind, mode: "review", token, reviewerName });

const itemPath = ({ kind, mode, token }, itemId) =>
  mode === "team" ? `${kind.api}/${itemId}` : `${kind.reviewApi}/${token}/${kind.plural}/${itemId}`;

// Every team board shares the "approval-board" prefix: a change on one kind's
// board also moves the other kind's "Script"/"Video" column for that creator.
const ALL_BOARDS_KEY = ["approval-board"];
const boardKey = ({ kind, mode, planId, token }) =>
  mode === "team" ? [...ALL_BOARDS_KEY, kind.noun, planId] : ["approval-review", kind.noun, token];
// Deliberately NOT under the board prefix — refreshing the board lists after an
// action mustn't also refetch the detail the action just returned.
const detailKey = ({ kind, mode, token }, itemId) => ["approval-item", kind.noun, mode === "team" ? "team" : token, itemId];
const boardsKey = (kind) => ["approval-boards", kind.noun];

const get = (url) => axios.get(url).then((r) => r.data);
const post = (url, body) => axios.post(url, body).then((r) => r.data);

export const shareUrlFor = (kind, token) => (token ? `${window.location.origin}${kind.reviewRoute}/${token}` : null);

export const useApprovalBoards = (kind) =>
  useQuery({ queryKey: boardsKey(kind), queryFn: () => get(kind.api), staleTime: 60 * 1000 });

export const useApprovalBoard = (kind, planId) =>
  useQuery({
    queryKey: boardKey(teamScope(kind, planId)),
    queryFn: () => get(`${kind.api}/plans/${planId}`),
    enabled: Boolean(planId),
  });

// A bad/rotated link is a 404 — no point retrying it.
export const useApprovalReview = (kind, token) =>
  useQuery({
    queryKey: boardKey(reviewScope(kind, token)),
    queryFn: () => get(`${kind.reviewApi}/${token}`),
    enabled: Boolean(token),
    retry: false,
  });

export const useApprovalDetail = (scope, itemId) =>
  useQuery({
    queryKey: detailKey(scope, itemId),
    queryFn: () => get(itemPath(scope, itemId)),
    enabled: Boolean(itemId),
    retry: false,
  });

// One workflow action (versions / decision / remarks). Every action answers
// with the fresh item detail, so the detail cache is replaced directly and
// only the row-level lists are refetched.
const useApprovalAction = (scope, itemId, action) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      post(`${itemPath(scope, itemId)}/${action}`, scope.mode === "review" ? { ...payload, reviewerName: scope.reviewerName } : payload),
    onSuccess: (detail) => {
      queryClient.setQueryData(detailKey(scope, itemId), detail);
      if (scope.mode === "team") {
        queryClient.invalidateQueries({ queryKey: ALL_BOARDS_KEY });
        queryClient.invalidateQueries({ queryKey: boardsKey(scope.kind) });
      } else {
        queryClient.invalidateQueries({ queryKey: boardKey(scope) });
      }
    },
    // 409 = someone else changed the item in between — pull the latest so
    // the person sees what changed instead of retrying blind.
    onError: (error) => {
      if (error?.response?.status === 409) queryClient.invalidateQueries({ queryKey: detailKey(scope, itemId) });
    },
  });
};

export const useApprovalActions = (scope, itemId) => ({
  saveVersion: useApprovalAction(scope, itemId, "versions"),
  decide: useApprovalAction(scope, itemId, "decision"),
  addRemark: useApprovalAction(scope, itemId, "remarks"),
});

// Video upload, straight to S3: ask the backend for a presigned PUT (it runs
// the same who-may-upload / still-open checks a save would), then PUT the file.
// XHR rather than fetch — fetch has no upload progress — and NOT the shared
// axios instance: its default headers (x-auth-token…) must not go to S3, and
// the presigned signature covers Content-Type, which must match file.type.
// `mimeType` is passed separately: some OS/browser pairs leave file.type empty.
// -> { promise: Promise<url>, abort() }
export const uploadToApproval = (scope, itemId, file, mimeType, onProgress) => {
  const xhr = new XMLHttpRequest();
  let aborted = false;
  const promise = post(`${itemPath(scope, itemId)}/upload-url`, { fileName: file.name, mimeType, size: file.size })
    .then(({ uploadUrl, url }) =>
      new Promise((resolve, reject) => {
        if (aborted) return reject(new Error("Upload cancelled"));
        xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(Math.round((e.loaded / e.total) * 100));
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve(url) : reject(new Error(`Upload failed (${xhr.status})`)));
        xhr.onerror = () => reject(new Error("Upload failed — check your connection and try again."));
        xhr.onabort = () => reject(new Error("Upload cancelled"));
        xhr.open("PUT", uploadUrl);
        // Content-Length is set by the browser from the body — S3 checks it
        // against the size signed into the URL.
        xhr.setRequestHeader("Content-Type", mimeType);
        xhr.send(file);
      })
    );
  return {
    promise,
    abort: () => {
      aborted = true;
      xhr.abort();
    },
  };
};

// ---- Team-only board management -------------------------------------------

const useBoardMutation = (kind, mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ALL_BOARDS_KEY });
      queryClient.invalidateQueries({ queryKey: boardsKey(kind) });
    },
  });
};

// Same payload as Plans' add-influencers ({listId, platform, profiles}) — fed by
// the shared AddInfluencersDrawer.
export const useAddApprovalCreators = (kind, planId) =>
  useBoardMutation(kind, (payload) => post(`${kind.api}/plans/${planId}/creators`, payload));

// Video board: add creators whose script the brand approved — all of them, or
// `planInfluencerIds` only (a script row's "Start video").
export const useImportApprovedCreators = (kind, planId) =>
  useBoardMutation(kind, (planInfluencerIds) =>
    post(`${kind.api}/plans/${planId}/creators/from-${kind.importsApprovedFrom.plural}`, planInfluencerIds ? { planInfluencerIds } : {})
  );

export const useRemoveApprovalItem = (kind) =>
  useBoardMutation(kind, (itemId) => axios.delete(`${kind.api}/${itemId}`).then((r) => r.data));

export const useRotateCreatorLink = (kind) =>
  useBoardMutation(kind, (itemId) => post(`${kind.api}/${itemId}/share-link/rotate`));

export const useRotateBrandLink = (kind, planId) =>
  useBoardMutation(kind, () => post(`${kind.api}/plans/${planId}/share-link/rotate`));

// Bulk WhatsApp template send to the picked creators; the server fills each creator's own
// review link / name / brand / plan into the {{creatorLink}} etc. tokens in a variable and records
// a WhatsApp Campaign, same as sending from a plan.
export const useSendApprovalWhatsapp = (kind, planId) =>
  useMutation({
    mutationFn: (payload) => post(`${kind.api}/plans/${planId}/whatsapp`, payload),
  });
